import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { PopoutContext } from '../PopoutContext';
import { PortalContainer } from './components/PortalContainer';
import { arrowElementModifier, offsetModifier, preventOverflowModifier, sameWidthModifier, } from './popperModifiers';
/**
 * Allows elements to have a popout element. Uses popper.js internally.
 * @example
 * ```tsx
 * // Usage
 * <Popout show onRenderPopout={() => <span>popout</span>}>
 *   <span>Text</span>
 * </Popout>
 * ```
 */
export const Popout = forwardRef((props, forwardedRef) => {
    const { show, arrow = false, sameWidth = true, offset = 0, padding = 0, direction = 'bottom', onRenderPopout, referenceElementClassName, popperElementClassName, arrowElementClassName, strategy = 'fixed', onRenderPopoutWrapper, withPortal = false, children, } = props;
    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const [arrowElement, setArrowElement] = useState(null);
    useImperativeHandle(forwardedRef, () => referenceElement);
    const modifiers = useMemo(() => {
        const modifiers = [preventOverflowModifier(padding), offsetModifier(offset)];
        if (arrow) {
            modifiers.push(arrowElementModifier(arrowElement));
        }
        if (sameWidth) {
            modifiers.push(sameWidthModifier());
        }
        return modifiers;
    }, [arrow, arrowElement, offset, padding, sameWidth]);
    const placement = useMemo(() => {
        if (direction === 'bottom') {
            return sameWidth ? 'bottom-start' : 'bottom';
        }
        else if (direction === 'top') {
            return sameWidth ? 'top-start' : 'top';
        }
        else if (direction === 'left') {
            return 'left';
        }
        else if (direction === 'right') {
            return 'right';
        }
        else {
            return direction;
        }
    }, [direction, sameWidth]);
    const options = useMemo(() => {
        return {
            placement,
            modifiers,
            strategy,
        };
    }, [modifiers, placement, strategy]);
    const { styles, attributes, forceUpdate } = usePopper(referenceElement, popperElement, options);
    // Since "forceUpdate" is not stable we save the current function in a ref that can be called when
    // "forceUpdate" from the context get invoked. This is unforunatley an issue with popper.js which
    // we cannot change at this time.
    const forceUpdateRef = useRef(forceUpdate);
    forceUpdateRef.current = forceUpdate;
    const context = useMemo(() => ({
        forceUpdate: () => {
            var _a;
            (_a = forceUpdateRef.current) === null || _a === void 0 ? void 0 : _a.call(forceUpdateRef);
        },
    }), []);
    function getContent() {
        if (!show) {
            return null;
        }
        const content = (_jsxs("div", Object.assign({ className: popperElementClassName, ref: setPopperElement, style: styles.popper }, attributes.popper, { children: [onRenderPopout(),
                arrow && (_jsx("div", { className: arrowElementClassName, ref: setArrowElement, style: styles.arrow }, void 0))] }), void 0));
        if (withPortal) {
            return _jsx(PortalContainer, { children: content }, void 0);
        }
        return content;
    }
    return (_jsx(PopoutContext.Provider, Object.assign({ value: context }, { children: _jsxs("div", Object.assign({ className: referenceElementClassName, ref: setReferenceElement }, { children: [children,
                onRenderPopoutWrapper === undefined ? getContent() : onRenderPopoutWrapper(getContent())] }), void 0) }), void 0));
});
Popout.displayName = 'Popout';
//# sourceMappingURL=Popout.js.map